// Endpoint wrappers against the backend's `/plans/*` contract. This is the
// only surface that hits the network — hooks should always go through here.

import type { AxiosInstance } from 'axios';
import type { EffectivePlan, InvoicePlan, Plan } from '../types';

export function plansApi(client: AxiosInstance) {
  return {
    /** Public — works without a token. Excludes Free by design. */
    listCatalog(): Promise<Plan[]> {
      return client
        .get<Plan[]>('/plans/catalog')
        .then((r) => r.data);
    },

    /** Authenticated — effective plan + usage + alert for the caller company. */
    getCurrentPlan(): Promise<EffectivePlan> {
      return client
        .get<EffectivePlan>('/plans/current')
        .then((r) => r.data);
    },

    /** Authenticated — invoice history (most recent first). */
    listInvoices(): Promise<InvoicePlan[]> {
      return client
        .get<InvoicePlan[]>('/plans/invoices')
        .then((r) => r.data);
    },

    /**
     * Authenticated — create a Stripe Checkout Session. Returns the hosted
     * checkout URL (caller should `window.location.assign(url)`).
     */
    createCheckout(input: {
      planId: number;
      quantity?: number;
      successUrl?: string;
      cancelUrl?: string;
    }): Promise<{ url: string; sessionId: string }> {
      return client
        .post<{ url: string; sessionId: string }>('/plans/checkout', input)
        .then((r) => r.data);
    },

    /**
     * Poll target after Stripe redirects to /billing/success. Returns
     * `{ status: 'pending' }` until the webhook finalises the EcInvoicePlan
     * row, then `{ status: 'ready', invoice }`.
     */
    confirmCheckout(
      sessionId: string,
    ): Promise<
      | { status: 'pending' }
      | { status: 'ready'; invoice: InvoicePlan }
    > {
      return client
        .get(`/plans/checkout/confirm/${encodeURIComponent(sessionId)}`)
        .then((r) => r.data);
    },
  };
}

export type PlansApi = ReturnType<typeof plansApi>;
